/**
 * Spec 041 — ThreadsTab component.
 *
 * Rendered inside CommandCenterPanel when the "Threads" tab is active (FR-003).
 * Displays the organization's task threads with title, status, owner,
 * last-activity timestamp, and an empty-state CTA (FR-003, SC-001).
 *
 * Consumes useThreads hook; does not poll independently.
 */

import React, { useState, useCallback } from 'react';
import type { EquanautThread } from '../hooks/useThreads';

interface ThreadsTabProps {
  threads: EquanautThread[];
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void;
  onCreateThread?: (title: string) => Promise<void>;
  onArchiveThread?: (id: string) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Status color map (mirrors fleet indicator palette)
// ---------------------------------------------------------------------------

const STATUS_COLOR: Record<string, string> = {
  active: '#33AA40',
  completed: '#4DC9FF',
  archived: '#8899A6',
};

const STATUS_BG: Record<string, string> = {
  active: 'rgba(51,170,64,0.15)',
  completed: 'rgba(77,201,255,0.15)',
  archived: 'rgba(136,153,166,0.15)',
};

function getErrorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

function fmtRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ThreadsTab: React.FC<ThreadsTabProps> = ({
  threads,
  isLoading,
  error,
  onRefresh,
  onCreateThread,
  onArchiveThread,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const selectedThread = threads.find(t => t.id === selectedId) ?? null;

  const handleCreate = useCallback(async () => {
    if (!newTitle.trim() || !onCreateThread) return;
    setCreateError(null);
    try {
      await onCreateThread(newTitle.trim());
      setNewTitle('');
      setIsCreating(false);
    } catch (err: unknown) {
      setCreateError(getErrorMessage(err, 'Failed to create thread'));
    }
  }, [newTitle, onCreateThread]);

  const handleArchive = useCallback(async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onArchiveThread) return;
    setArchiveError(null);
    try {
      await onArchiveThread(id);
      if (selectedId === id) setSelectedId(null);
    } catch (err: unknown) {
      setArchiveError(getErrorMessage(err, 'Failed to archive thread'));
    }
  }, [onArchiveThread, selectedId]);

  const handleThreadKeyDown = useCallback((threadId: string, e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    setSelectedId(prev => (prev === threadId ? null : threadId));
  }, []);

  // ─── Styles ───────────────────────────────────────────────────────────────

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    flexShrink: 0,
  };

  const headerLabelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 600,
    color: '#8899A6',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  const iconBtnStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#8899A6',
    padding: '2px 4px',
    borderRadius: '3px',
    fontSize: '16px',
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
  };

  const scrollStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: '4px 0',
  };

  const emptyStyle: React.CSSProperties = {
    padding: '24px 16px',
    textAlign: 'center',
    color: '#8899A6',
    fontSize: '12px',
  };

  const emptyCtaStyle: React.CSSProperties = {
    display: 'inline-block',
    marginTop: '12px',
    padding: '6px 14px',
    borderRadius: '5px',
    background: 'rgba(51,170,64,0.15)',
    color: '#4DC95A',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
    border: '1px solid rgba(51,170,64,0.3)',
  };

  const threadRowStyle = (isSelected: boolean): React.CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    padding: '8px 12px',
    cursor: 'pointer',
    backgroundColor: isSelected ? 'rgba(51,170,64,0.08)' : 'transparent',
    borderLeft: isSelected ? '2px solid #33AA40' : '2px solid transparent',
    transition: 'background 0.15s',
  });

  const threadTitleStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 500,
    color: '#C8D6DF',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const threadMetaStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '10px',
    color: '#8899A6',
  };

  const statusBadgeStyle = (status: string): React.CSSProperties => ({
    padding: '1px 5px',
    borderRadius: '3px',
    fontSize: '9px',
    fontWeight: 700,
    textTransform: 'uppercase',
    backgroundColor: STATUS_BG[status] ?? 'rgba(136,153,166,0.15)',
    color: STATUS_COLOR[status] ?? '#8899A6',
  });

  const detailPanelStyle: React.CSSProperties = {
    padding: '12px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    backgroundColor: 'rgba(0,0,0,0.15)',
    flexShrink: 0,
  };

  const createFormStyle: React.CSSProperties = {
    padding: '8px 12px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    backgroundColor: 'rgba(0,0,0,0.1)',
    flexShrink: 0,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '6px 8px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '4px',
    color: '#C8D6DF',
    fontSize: '12px',
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: '6px',
  };

  const createBtnStyle: React.CSSProperties = {
    padding: '5px 10px',
    backgroundColor: '#33AA40',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
    marginRight: '6px',
  };

  const cancelBtnStyle: React.CSSProperties = {
    padding: '5px 10px',
    backgroundColor: 'transparent',
    color: '#8899A6',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '4px',
    fontSize: '11px',
    cursor: 'pointer',
  };

  const errorStyle: React.CSSProperties = {
    padding: '4px 12px',
    fontSize: '11px',
    color: '#FF5656',
    backgroundColor: 'rgba(255,86,86,0.08)',
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={containerStyle}>
      {/* Header row */}
      <div style={headerStyle}>
        <span style={headerLabelStyle}>Threads</span>
        <div style={{ display: 'flex', gap: '4px' }}>
          <button
            type="button"
            style={iconBtnStyle}
            onClick={onRefresh}
            title="Refresh threads"
            aria-label="Refresh threads"
          >
            ↻
          </button>
          {onCreateThread && (
            <button
              type="button"
              style={iconBtnStyle}
              onClick={() => setIsCreating(v => !v)}
              title="New thread"
              aria-label="New thread"
            >
              +
            </button>
          )}
        </div>
      </div>

      {/* Error banner */}
      {error && <div style={errorStyle}>{error}</div>}
      {archiveError && <div style={errorStyle}>{archiveError}</div>}

      {/* Thread list */}
      <div style={scrollStyle}>
        {isLoading && threads.length === 0 ? (
          <div style={emptyStyle}>Loading threads…</div>
        ) : threads.length === 0 ? (
          <div style={emptyStyle}>
            <div>No active threads in the past 30 days.</div>
            {onCreateThread && (
              <button
                type="button"
                style={emptyCtaStyle}
                onClick={() => setIsCreating(true)}
              >
                + Create thread
              </button>
            )}
          </div>
        ) : (
          threads.map(thread => (
            <div
              key={thread.id}
              role="button"
              tabIndex={0}
              style={threadRowStyle(thread.id === selectedId)}
              onClick={() => setSelectedId(prev => (prev === thread.id ? null : thread.id))}
              onKeyDown={e => handleThreadKeyDown(thread.id, e)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={threadTitleStyle}>{thread.title}</span>
                {onArchiveThread && thread.status !== 'archived' && (
                  <button
                    type="button"
                    style={{ ...iconBtnStyle, marginLeft: 'auto', fontSize: '12px' }}
                    title="Archive thread"
                    aria-label={`Archive thread ${thread.title}`}
                    onClick={e => handleArchive(thread.id, e)}
                  >
                    ×
                  </button>
                )}
              </div>
              <div style={threadMetaStyle}>
                <span style={statusBadgeStyle(thread.status)}>{thread.status}</span>
                {thread.ownerDisplay && <span>{thread.ownerDisplay}</span>}
                <span style={{ marginLeft: 'auto' }}>{fmtRelative(thread.updatedAt)}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create form */}
      {isCreating && (
        <div style={createFormStyle}>
          <input
            style={inputStyle}
            placeholder="Thread title…"
            value={newTitle}
            autoFocus
            onChange={e => setNewTitle(e.target.value)}
              aria-label="Thread title"
              onKeyDown={e => { if (e.key === 'Enter') void handleCreate(); }}
          />
          {createError && <div style={{ color: '#FF5656', fontSize: '10px', marginBottom: '4px' }}>{createError}</div>}
          <div>
            <button type="button" style={createBtnStyle} onClick={handleCreate}>Create</button>
            <button type="button" style={cancelBtnStyle} onClick={() => { setIsCreating(false); setNewTitle(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Detail panel for selected thread */}
      {selectedThread && (
        <div style={detailPanelStyle}>
          <div style={{ fontSize: '11px', color: '#8899A6', marginBottom: '4px' }}>Thread detail</div>
          <div style={{ fontSize: '12px', color: '#C8D6DF', fontWeight: 500, marginBottom: '4px' }}>
            {selectedThread.title}
          </div>
          <div style={{ fontSize: '10px', color: '#8899A6' }}>
            ID: <span style={{ fontFamily: 'monospace' }}>{selectedThread.id.slice(0, 12)}…</span>
          </div>
          <div style={{ fontSize: '10px', color: '#8899A6', marginTop: '2px' }}>
            Created: {fmtRelative(selectedThread.createdAt)} · Source: {selectedThread.source}
          </div>
          {selectedThread.orchestratorUrl && (
            <div style={{ fontSize: '10px', marginTop: '4px' }}>
              <a
                href={selectedThread.orchestratorUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#4DC95A' }}
              >
                Open in orchestrator ↗
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ThreadsTab;
